/**
 * Utilitários puros para configuração de relatórios de grupos de veículos.
 * Reutilizável em qualquer tela que opere sobre o shape de ReportConfig.
 */

import { FREQUENCIA, TIPO_EXCEL } from "./enums";

// ---------------------------------------------------------------------------
// Normalização de respostas da API
// ---------------------------------------------------------------------------

/**
 * Extrai o texto de e-mail de um valor que pode ser string ou objeto.
 * @param {string|object} value
 * @returns {string}
 */
export const extractEmailText = (value) => {
  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object") {
    return (
      value.email ||
      value.address ||
      value.value ||
      value.text ||
      value.label ||
      ""
    );
  }

  return "";
};

/**
 * Normaliza a lista de e-mails retornada pela API para o shape interno:
 * `{ id, email, isValid: "S"|"N", statusText }`.
 * @param {any} response
 * @returns {Array<{id: string, email: string, isValid: "S"|"N", statusText: string}>}
 */
export const normalizeEmailList = (response) => {
  const responseList =
    (Array.isArray(response) && response) ||
    (Array.isArray(response?.items) && response.items) ||
    (Array.isArray(response?.result) && response.result) ||
    (Array.isArray(response?.data) && response.data) ||
    (response && typeof response === "object" ? [response] : []);

  return responseList
    .map((entry) => {
      const rawIsValid =
        entry?.isValid ?? entry?.validated ?? entry?.isValidated;
      const normalizedEmail = extractEmailText(
        entry?.email ??
          entry?.address ??
          entry?.value ??
          entry?.text ??
          entry?.label
      );

      return {
        id:
          entry?.id ??
          entry?.emailId ??
          entry?.emailConfigurationId ??
          entry?.vehicleGroupEmailId,
        email: normalizedEmail,
        isValid:
          rawIsValid === "S" || rawIsValid === true
            ? "S"
            : rawIsValid === "N" || rawIsValid === false
            ? "N"
            : "N",
        statusText:
          rawIsValid === "N" || rawIsValid === false ? "Pendente" : "",
      };
    })
    .filter((entry) => entry.id && entry.email);
};

// ---------------------------------------------------------------------------
// Mapeamento de dados da API para o formato de ReportConfig
// ---------------------------------------------------------------------------

/**
 * Constrói o array de configuração de relatórios a partir da resposta da API
 * do endpoint `fetchGroupReportsConfiguration`.
 * @param {object} groupData
 * @returns {Array}
 */
export const buildReportConfigFromApi = (groupData) => {
  const mapPeriod = (value) =>
    value === "S" ? FREQUENCIA.SEMANAL : FREQUENCIA.DIARIO;

  return [
    {
      id: "positionKmzPeriod",
      ativo: !!groupData?.positionKmzPeriod,
      frequencia: mapPeriod(groupData?.positionKmzPeriod),
    },
    {
      id: "positionPdfPeriod",
      ativo: !!groupData?.positionPdfPeriod,
      frequencia: mapPeriod(groupData?.positionPdfPeriod),
    },
    {
      id: "positionXlsPeriod",
      ativo: !!groupData?.positionXlsPeriod,
      frequencia: mapPeriod(groupData?.positionXlsPeriod),
      tipoExcel: TIPO_EXCEL.ABA_UNICA,
    },
    {
      id: "activitySummaryPdfPeriod",
      ativo: !!groupData?.activitySummaryPdfPeriod,
      frequencia: mapPeriod(groupData?.activitySummaryPdfPeriod),
    },
    {
      id: "activitySummaryXlsPeriod",
      ativo: !!groupData?.activitySummaryXlsPeriod,
      frequencia: mapPeriod(groupData?.activitySummaryXlsPeriod),
      tipoExcel: TIPO_EXCEL.ABA_UNICA,
    },
    {
      id: "excerptSummaryPdfPeriod",
      ativo: !!groupData?.excerptSummaryPdfPeriod,
      frequencia: mapPeriod(groupData?.excerptSummaryPdfPeriod),
    },
    {
      id: "excerptSummaryXlsPeriod",
      ativo: !!groupData?.excerptSummaryXlsPeriod,
      frequencia: mapPeriod(groupData?.excerptSummaryXlsPeriod),
      tipoExcel: TIPO_EXCEL.ABA_UNICA,
    },
    {
      // codeReport 26 — leitura: campo direto > variantes antigas
      id: "visitsSummaryPdfPeriod",
      ativo: !!(
        groupData?.visitsSummaryPdfPeriod ??
        groupData?.summaryVisitsByVehiclesPdfPeriod
      ),
      frequencia: mapPeriod(
        groupData?.visitsSummaryPdfPeriod ??
        groupData?.summaryVisitsByVehiclesPdfPeriod
      ),
    },
    {
      // codeReport 25
      id: "visitsSummaryXlsPeriod",
      ativo: !!(
        groupData?.visitsSummaryXlsPeriod ??
        groupData?.summaryVisitsByVehiclesXlsPeriod
      ),
      frequencia: mapPeriod(
        groupData?.visitsSummaryXlsPeriod ??
        groupData?.summaryVisitsByVehiclesXlsPeriod
      ),
      tipoExcel: TIPO_EXCEL.ABA_UNICA,
    },
    {
      // codeReport 31
      id: "visitsAmountPdfPeriod",
      ativo: !!(
        groupData?.visitsAmountPdfPeriod ??
        groupData?.visitVehicleSummaryPdfPeriod ??
        groupData?.vehicleVisitSummaryPdfPeriod
      ),
      frequencia: mapPeriod(
        groupData?.visitsAmountPdfPeriod ??
        groupData?.visitVehicleSummaryPdfPeriod ??
        groupData?.vehicleVisitSummaryPdfPeriod
      ),
    },
    {
      // codeReport 30
      id: "visitsAmountXlsPeriod",
      ativo: !!(
        groupData?.visitsAmountXlsPeriod ??
        groupData?.visitVehicleSummaryXlsPeriod ??
        groupData?.vehicleVisitSummaryXlsPeriod
      ),
      frequencia: mapPeriod(
        groupData?.visitsAmountXlsPeriod ??
        groupData?.visitVehicleSummaryXlsPeriod ??
        groupData?.vehicleVisitSummaryXlsPeriod
      ),
      tipoExcel: TIPO_EXCEL.ABA_UNICA,
    },
    {
      // codeReport 36
      id: "speedSummaryPdfPeriod",
      ativo: !!(
        groupData?.speedSummaryPdfPeriod ??
        groupData?.summaryHighspeedPDFPeriod ??
        groupData?.summaryHighspeedPdfPeriod
      ),
      frequencia: mapPeriod(
        groupData?.speedSummaryPdfPeriod ??
        groupData?.summaryHighspeedPDFPeriod ??
        groupData?.summaryHighspeedPdfPeriod
      ),
      speedHigh: 30,
      typeHigh: 1,
    },
    {
      // codeReport 35
      id: "speedSummaryXlsPeriod",
      ativo: !!(
        groupData?.speedSummaryXlsPeriod ??
        groupData?.summaryHighspeedXLSPeriod ??
        groupData?.summaryHighspeedXlsPeriod
      ),
      frequencia: mapPeriod(
        groupData?.speedSummaryXlsPeriod ??
        groupData?.summaryHighspeedXLSPeriod ??
        groupData?.summaryHighspeedXlsPeriod
      ),
      tipoExcel: TIPO_EXCEL.ABA_UNICA,
      speedHigh: 30,
      typeHigh: 1,
    },
    {
      // codeReport 40
      id: "maintenanceSummaryPdfPeriod",
      ativo: !!(
        groupData?.maintenanceSummaryPdfPeriod ??
        groupData?.summaryMaintenancesPdfPeriod
      ),
      frequencia: mapPeriod(
        groupData?.maintenanceSummaryPdfPeriod ??
        groupData?.summaryMaintenancesPdfPeriod
      ),
    },
    {
      // codeReport 41
      id: "maintenanceSummaryXlsPeriod",
      ativo: !!(
        groupData?.maintenanceSummaryXlsPeriod ??
        groupData?.summaryMaintenancesXlsPeriod
      ),
      frequencia: mapPeriod(
        groupData?.maintenanceSummaryXlsPeriod ??
        groupData?.summaryMaintenancesXlsPeriod
      ),
      tipoExcel: TIPO_EXCEL.ABA_UNICA,
    },
  ];
};

/**
 * Garante que os itens de alta velocidade (`speedSummaryPdfPeriod` e
 * `speedSummaryXlsPeriod`) existam no array de configuração e possuam
 * valores padrão para `speedHigh` e `typeHigh`.
 * @param {Array} configs
 * @returns {Array}
 */
export const ensureHighSpeedDefaults = (configs) => {
  const nextConfigs = Array.isArray(configs) ? [...configs] : [];

  ["speedSummaryPdfPeriod", "speedSummaryXlsPeriod"].forEach((reportId) => {
    const index = nextConfigs.findIndex((item) => item.id === reportId);
    if (index >= 0) {
      nextConfigs[index] = {
        ...nextConfigs[index],
        speedHigh: nextConfigs[index].speedHigh ?? 30,
        typeHigh: nextConfigs[index].typeHigh ?? 1,
      };
    } else {
      nextConfigs.push({
        id: reportId,
        ativo: false,
        frequencia: FREQUENCIA.DIARIO,
        speedHigh: 30,
        typeHigh: 1,
      });
    }
  });

  return nextConfigs;
};
